import { gql, useQuery } from '@apollo/client';
import React, { useCallback, useMemo, useState } from 'react';

import { AreaStack } from '@visx/shape';
import { SeriesPoint } from '@visx/shape/lib/types';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { scaleLinear, scaleOrdinal, scaleTime } from '@visx/scale';
import { timeFormat, timeParse } from 'd3-time-format';
import { defaultStyles, Tooltip, useTooltip } from '@visx/tooltip';
import { Box, Flex, Text } from 'rebass';
import colors from '../../theme/colors';
import theme from '../../theme';
import Legend from '../Chart/Legend';
import { assignColumnAccessor } from '../../hooks/useTablePages';
import papaparse from 'papaparse';
import { downloadFile } from '../../helpers/download';
import Button from '../Button/Button';

const NOTICES_SENT_QUERY = gql`
  query NoticesSent($where: JSON) {
    noticeAssemblies(
      where: $where
      groupBy: ["createdMonth", "notice.profile.id"]
      aggregate: [
        { col: "id", type: DISTINCT, as: "noticesSent" }
        { col: "assemblyId", type: DISTINCT, as: "distinctAssemblies" }
      ]
      orderBy: [{ col: "notice.profile.id" }, { col: "createdMonth" }]
      limit: null
    ) {
      createdMonth
      notice {
        profile {
          id
          name
        }
      }
      agg
    }
  }
`;

type NoticeSentData = {
  noticeAssemblies: {
    createdMonth: string;
    notice: {
      profile: {
        id: string;
        name: string;
      };
    };
    agg: {
      noticesSent: number;
      distinctAssemblies: number;
    };
  }[];
};

type BarStackProps = {
  width: number;
  height: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  events?: boolean;
};

type Datum = { month: number } & Record<string, number>;

const defaultMargin = { top: 40, right: 0, bottom: 0, left: 0 };

const tooltipStyles: React.CSSProperties = {
  ...defaultStyles,
  minWidth: 240,
  backgroundColor: 'white',
  color: colors.text,
  position: 'absolute',
  borderRadius: theme.radii.default,
  boxShadow: theme.shadows.menu,
  padding: theme.space[3],
  opacity: 0.95,
  transition: 'all .3s ease',
};

const parseDate = timeParse('%Y-%m');
const format = timeFormat("%b '%y");
const longFormat = timeFormat('%B %Y');
const formatDate = (month: number | Date | { valueOf(): number }) =>
  format(new Date(month.valueOf()));
const formatDateLong = (month: number | Date | { valueOf(): number }) =>
  longFormat(new Date(month.valueOf()));

// accessors
const getMonth = (d: SeriesPoint<Datum>) => new Date(d.data.month).getTime();

let tooltipTimeout: number;

const Visx = ({
  width,
  height,
  events = true,
  margin = defaultMargin,
}: BarStackProps) => {
  const [metric, setMetric] = useState<'noticesSent' | 'distinctAssemblies'>(
    'noticesSent'
  );

  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const dateRange = {
    between: [
      Date.parse(`${year - 1}-01-01`) / 1000,
      Date.parse(`${year}-12-31`) / 1000,
    ],
  };

  const { data: noticesSentData } = useQuery<NoticeSentData>(
    NOTICES_SENT_QUERY,
    {
      variables: {
        where: {
          created: dateRange,
        },
      },
      fetchPolicy: 'no-cache',
    }
  );

  const noticesSentByMonth = noticesSentData?.noticeAssemblies || [];

  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<Datum>();

  // bounds
  const xMax = width;
  const yMax = height - margin.top - 25;

  const senders: string[] = Array.from(
    new Set(noticesSentByMonth.map((row) => row.notice.profile.name)).values()
  );

  const [disabledKeys, setDisabledKeys] = useState(
    {} as Record<string, boolean | undefined>
  );

  const toggleKey = useCallback((key: string) => {
    setDisabledKeys((oldValue) => {
      return {
        ...oldValue,
        [key]: !oldValue[key],
      };
    });
  }, []);

  const keys = senders.filter((sender) => !disabledKeys[sender]);

  const months: string[] = Array.from(
    new Set(noticesSentByMonth.map((row) => row.createdMonth)).values()
  );
  months.sort();

  const data = useMemo(
    () =>
      months.reduce((allTotals, currentMonth) => {
        const monthEntry = noticesSentByMonth
          .filter((row) => row.createdMonth === currentMonth)
          .reduce(
            (monthEntry, current) => {
              monthEntry[current.notice.profile.name] = current.agg[metric];
              return monthEntry;
            },
            {
              month: (parseDate(currentMonth) as Date).getTime(),
              ...senders.reduce(
                (current, sender) => {
                  current[sender] = 0;
                  return current;
                },
                {} as Record<string, number>
              ),
            } as Datum
          );
        allTotals.push(monthEntry);
        return allTotals;
      }, [] as Datum[]),
    [noticesSentByMonth]
  );

  const monthTotals = useMemo(
    () =>
      data.reduce((allTotals, currentMonth) => {
        const monthTotal = keys.reduce((total, sender) => {
          return total + currentMonth[sender];
        }, 0);
        allTotals.push(monthTotal);
        return allTotals;
      }, [] as number[]),
    [data, disabledKeys]
  );

  if (width < 10 || !noticesSentByMonth) return <></>;

  const dates: number[] = months.map((month) =>
    (parseDate(month) as Date).getTime()
  );

  // scales
  const dateScale = scaleTime<number>({
    domain: [Math.min(...dates), Math.max(...dates)],
    range: [0, xMax],
    nice: true,
  });

  const yScale = scaleLinear<number>({
    domain: [0, Math.max(...monthTotals)],
    range: [yMax, 0],
    nice: true,
  });

  const colorList = [
    colors.statusRed,
    colors.statusOrange,
    colors.statusYellow,
    colors.statusGreen,
    colors.statusBlue,
    colors.statusPurple,
    colors.statusDefault,
    colors.statusPrimary,
    colors.statusSuccess,
  ];

  const colorMap: Record<string, string> = senders.reduce(
    (current, sender, index) => {
      current[sender] = colorList[index] as string; // TODO: wrap array
      return current;
    },
    {} as Record<string, string>
  );

  const colorScale = scaleOrdinal<string, string>({
    domain: Object.keys(colorMap),
    range: Object.values(colorMap),
  });

  const columns = [
    {
      header: 'Month',
      id: 'month',
      accessor: (row: Datum) => {
        return formatDateLong(row.month);
      },
    },
    ...keys.map((key) => {
      return {
        header: key,
        accessor: key,
      };
    }),
  ].map(assignColumnAccessor);

  const downloadCsv = () => {
    const filename = 'notices-sent.csv';
    const csv = papaparse.unparse({
      fields: columns.map((column) => column.header),
      data: data.map((item) => columns.map((column) => column.accessor(item))),
    });
    downloadFile(filename, csv);
  };

  return (
    // relative position is needed for correct tooltip positioning
    <Box
      sx={{
        position: 'relative',
        '.test-status-bar:hover': {
          opacity: 0.8,
        },
      }}
    >
      <Flex
        sx={{
          position: 'absolute',
          width: '100%',
          top: 1,
          justifyContent: 'center',
          fontSize: '14px',
        }}
      >
        <Legend
          colorScale={colorScale}
          disabledKeys={disabledKeys}
          toggleKey={toggleKey}
        />
      </Flex>

      <svg
        width={width}
        height={height}
        onMouseLeave={() => {
          tooltipTimeout = window.setTimeout(() => {
            hideTooltip();
          }, 300);
        }}
        onMouseMove={(event) => {
          if (tooltipTimeout) {
            clearTimeout(tooltipTimeout);
          }
          const x = event.nativeEvent.offsetX;
          const month = data.find(
            (datum) =>
              formatDate(datum.month) ===
              formatDate(dateScale.invert(x).getTime() + 864000000)
          );
          if (!month) {
            return;
          }
          const top = 0;
          const left = Math.max(
            Math.min(
              dateScale(month.month) as number,
              width - (tooltipStyles.minWidth as number) - 40
            ),
            10
          );
          const opts = {
            tooltipData: month,
            tooltipTop: top,
            tooltipLeft: left,
          };
          showTooltip(opts);
        }}
      >
        <Group top={margin.top}>
          <AreaStack<Datum>
            data={data}
            keys={keys}
            x={(d) => dateScale(getMonth(d)) as number}
            y0={(d) => yScale(d[0]) as number}
            y1={(d) => yScale(d[1]) as number}
          >
            {({ stacks, path }) =>
              stacks.map((stack) => (
                <path
                  key={stack.key}
                  d={path(stack) || ''}
                  stroke={colorMap[stack.key]}
                  fill={colorMap[stack.key]}
                  onClick={() => {
                    if (events) alert(`${stack.key}`);
                  }}
                />
              ))
            }
          </AreaStack>
        </Group>
        <AxisBottom
          top={yMax + margin.top}
          scale={dateScale}
          tickFormat={formatDate}
          stroke={colors.text}
          tickStroke={colors.text}
          // hideTicks
          hideAxisLine
          tickLabelProps={() => ({
            fill: colors.text,
            fontSize: 12,
            textAnchor: 'middle',
          })}
        />
      </svg>
      <Tooltip
        key={Math.random()} // update tooltip bounds each render
        top={tooltipTop}
        left={tooltipLeft}
        offsetLeft={0}
        offsetTop={10}
        style={tooltipStyles}
        hidden={!tooltipOpen}
      >
        {tooltipData && (
          <>
            <Text variant="heading2">{formatDateLong(tooltipData.month)}</Text>
            {keys.map((sender) => (
              <Flex mt={2} justifyContent="space-between" key={sender}>
                <Text color={colorMap[sender]}>{sender}</Text>
                <Text fontWeight="bold">{tooltipData[sender]}</Text>
              </Flex>
            ))}
          </>
        )}
      </Tooltip>
      <Button
        sx={{ position: 'absolute', top: 0, right: 0 }}
        icon="download"
        onClick={downloadCsv}
      />
    </Box>
  );
};

// export default TestStatusWidget
export default Visx;
